// src/performance-schemas.ts
import { z } from "zod";
var performancePermissions = [
  "performance_templates.read",
  "performance_templates.manage",
  "work_plans.read",
  "work_plans.manage",
  "work_plans.update_own",
  "performance_reviews.read",
  "performance_reviews.write_own",
  "performance_reviews.manage",
  "performance_reviews.publish"
];
var masteryLevels = ["POOR", "BASIC", "INTERMEDIATE", "ADVANCED", "EXPERT"];
var masterySchema = z.enum(masteryLevels);
var performanceVersionSchema = z.number().int().positive();
var plainText = z.string().regex(/^(?![\s\S]*<\/?[A-Za-z][^>]*>)[\s\S]*$/, "\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0439\u0442\u0435 \u043E\u0431\u044B\u0447\u043D\u044B\u0439 \u0442\u0435\u043A\u0441\u0442 \u0431\u0435\u0437 HTML");
var performanceTextSchema = plainText.max(1e4);
var title = plainText.trim().min(1).max(200);
var key = z.string().regex(/^[a-zA-Z0-9_-]+$/).min(1).max(100);
var weight = z.number().int().min(0).max(1e4);
var uuid = z.uuid();
var reason = plainText.trim().min(1).max(2e3);
var performanceUrlSchema = z.url({ protocol: /^https?$/ }).max(2e3);
var performanceEvidenceSchema = z.array(performanceUrlSchema).max(20);
var performanceActionSchema = z.strictObject({ version: performanceVersionSchema });
var performanceReasonSchema = performanceActionSchema.extend({ reason });
var performanceListSchema = z.strictObject({
  cursor: z.string().max(2e3).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  organizationId: uuid.optional(),
  status: z.string().max(30).optional()
});
var performanceCatalogCreateSchema = z.strictObject({
  organizationId: uuid,
  code: key,
  name: title,
  ownerEmployeeId: uuid.nullable().default(null)
});
var roleProfileCreateSchema = performanceCatalogCreateSchema.extend({
  specialisationId: uuid.nullish(),
  positionId: uuid.nullish()
}).refine((input) => Boolean(input.positionId) !== Boolean(input.specialisationId), {
  path: ["positionId"],
  message: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0434\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u044C \u0434\u043B\u044F \u0448\u0430\u0431\u043B\u043E\u043D\u0430"
});
var performanceCatalogPatchSchema = z.strictObject({
  version: performanceVersionSchema,
  name: title.optional(),
  ownerEmployeeId: uuid.nullable().optional()
});
var performanceCriterionSchema = z.strictObject({
  key,
  title,
  description: performanceTextSchema,
  weight,
  required: z.boolean(),
  employeeVisible: z.boolean().default(true)
});
var skillContentSchema = z.strictObject({
  schemaVersion: z.literal(1),
  description: performanceTextSchema,
  category: title,
  levels: z.array(
    z.strictObject({ level: masterySchema, indicators: z.array(performanceTextSchema).max(20) })
  ).max(5),
  resources: z.array(performanceUrlSchema).max(20)
});
var evaluationContentSchema = z.strictObject({
  schemaVersion: z.literal(1),
  selfReviewRequired: z.boolean(),
  sectionWeights: z.strictObject({ DELIVERABLES: weight, SKILLS: weight, VALUES: weight }),
  grades: z.array(
    z.strictObject({
      grade: z.number().int().min(1).max(5),
      title,
      description: performanceTextSchema
    })
  ).max(5),
  masteryMapping: z.array(
    z.strictObject({
      expected: masterySchema,
      actual: masterySchema,
      grade: z.number().int().min(1).max(5)
    })
  ).max(25),
  values: z.array(performanceCriterionSchema).max(100),
  questionGroups: z.array(
    z.strictObject({
      key,
      title,
      description: performanceTextSchema,
      reviewerTypes: z.array(z.enum(["SELF", "LINE_MANAGER"])).min(1).max(2),
      questions: z.array(
        z.strictObject({
          key,
          title,
          description: performanceTextSchema,
          type: z.enum(["TEXT", "CHOICE", "SCALE"]),
          required: z.boolean(),
          employeeVisible: z.boolean(),
          options: z.array(title).max(20)
        })
      ).max(100)
    })
  ).max(20),
  feedbackEmployeeVisible: z.boolean()
});
var workTaskDefinitionSchema = z.strictObject({
  taskKey: key,
  title,
  description: performanceTextSchema,
  expectedResult: performanceTextSchema,
  acceptanceCriteria: performanceTextSchema,
  isRequired: z.boolean(),
  evidenceRequired: z.boolean(),
  dueOffsetDays: z.number().int().min(0).max(3660).nullable(),
  criteria: z.array(performanceCriterionSchema).max(3),
  weight
});
var roleTaskTemplateSchema = workTaskDefinitionSchema.omit({ weight: true, criteria: true }).extend({
  levels: z.array(
    z.strictObject({
      seniorityId: uuid,
      weight,
      expectedResult: performanceTextSchema,
      criteria: z.array(performanceCriterionSchema).max(3)
    })
  ).max(20)
});
var roleSkillExpectationSchema = z.strictObject({
  skillVersionId: uuid,
  seniorityId: uuid,
  expectedLevel: masterySchema,
  weight,
  required: z.boolean()
});
var roleProfileContentSchema = z.strictObject({
  schemaVersion: z.literal(1),
  mission: performanceTextSchema,
  seniorityIds: z.array(uuid).max(20),
  evaluationTemplateVersionId: uuid.nullable(),
  tasks: z.array(roleTaskTemplateSchema).max(100),
  skillExpectations: z.array(roleSkillExpectationSchema).max(2e3)
});
var versionCreate = z.strictObject({
  version: performanceVersionSchema,
  copyFromVersionId: uuid.optional()
});
var skillVersionCreateSchema = versionCreate.extend({
  content: skillContentSchema.optional()
});
var evaluationVersionCreateSchema = versionCreate.extend({
  content: evaluationContentSchema.optional()
});
var roleVersionCreateSchema = versionCreate.extend({
  content: roleProfileContentSchema.optional()
});
var skillVersionPatchSchema = performanceActionSchema.extend({
  content: skillContentSchema
});
var evaluationVersionPatchSchema = performanceActionSchema.extend({
  content: evaluationContentSchema
});
var roleVersionPatchSchema = performanceActionSchema.extend({
  content: roleProfileContentSchema
});
var allowedSenioritiesSchema = performanceActionSchema.extend({
  seniorityIds: z.array(uuid).min(1).max(20)
});
var profileAssignmentSchema = z.strictObject({
  roleProfileVersionId: uuid,
  expectedEmployeeVersion: performanceVersionSchema,
  reason
});
var cycleCreateSchema = z.strictObject({
  organizationId: uuid,
  name: title,
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  reviewOpenAt: z.iso.datetime(),
  reviewDueAt: z.iso.datetime()
});
var cyclePatchSchema = cycleCreateSchema.omit({ organizationId: true }).partial().extend({
  version: performanceVersionSchema,
  reason: reason.optional()
});
var workPlanCreateSchema = z.strictObject({
  cycleId: uuid,
  assignmentId: uuid,
  replacesPlanId: uuid.optional()
});
var workPlanPatchSchema = performanceActionSchema.extend({ title: title.optional() });
var workTaskCreateSchema = performanceActionSchema.extend({
  definition: workTaskDefinitionSchema,
  dueDate: z.iso.date()
});
var workTaskPatchSchema = performanceActionSchema.extend({
  definition: workTaskDefinitionSchema,
  dueDate: z.iso.date()
});
var workProgressSchema = performanceActionSchema.extend({
  result: performanceTextSchema,
  evidence: performanceEvidenceSchema
});
var reviewCreateSchema = performanceActionSchema;
var reviewReviewerSchema = performanceReasonSchema.extend({
  reviewerAccountId: uuid,
  submissionVersion: performanceVersionSchema
});
var reviewAnswerSchema = z.strictObject({
  key: z.string().min(1).max(300),
  value: z.union([z.number().int().min(1).max(5), performanceTextSchema, z.null()]),
  comment: performanceTextSchema
});
var reviewAnswersSchema = z.strictObject({
  answers: z.array(reviewAnswerSchema).max(600),
  strengths: performanceTextSchema,
  development: performanceTextSchema
});
var reviewSubmissionPatchSchema = performanceActionSchema.extend({
  content: reviewAnswersSchema
});
var reviewFinalizeSchema = performanceActionSchema.extend({
  finalGrade: z.number().int().min(1).max(5),
  reason: performanceTextSchema.optional()
});
var reviewCorrectionSchema = performanceReasonSchema.extend({
  reviewDueAt: z.iso.datetime()
});
var resolvedProfileSchema = z.strictObject({
  schemaVersion: z.literal(1),
  profileVersionId: uuid,
  seniorityId: uuid,
  specialisationId: uuid.nullable(),
  positionId: uuid.nullable().optional(),
  evaluationTemplateVersionId: uuid,
  mission: performanceTextSchema,
  tasks: z.array(workTaskDefinitionSchema).max(100),
  skills: z.array(
    z.strictObject({
      skillId: uuid,
      skillVersionId: uuid,
      name: title,
      expectedLevel: masterySchema,
      weight,
      required: z.boolean(),
      content: skillContentSchema
    })
  ).max(100),
  evaluationTemplate: evaluationContentSchema
});
var workTaskSnapshotSchema = z.strictObject({
  id: uuid,
  definition: workTaskDefinitionSchema,
  status: z.enum(["TODO", "IN_PROGRESS", "SUBMITTED", "ACCEPTED", "EXCLUDED"]),
  result: performanceTextSchema,
  evidence: performanceEvidenceSchema,
  dueDate: z.iso.date()
});
var reviewSnapshotSchema = z.strictObject({
  schemaVersion: z.literal(1),
  profile: resolvedProfileSchema,
  employee: z.strictObject({
    specialisationId: uuid.nullable(),
    positionId: uuid.nullable().optional(),
    seniorityId: uuid,
    departmentId: uuid,
    teamIds: z.array(uuid),
    lineManagerId: uuid.nullable()
  }),
  tasks: z.array(workTaskSnapshotSchema).max(100)
});
var performanceSchemas = {
  PerformanceAction: performanceActionSchema,
  PerformanceReason: performanceReasonSchema,
  PerformanceCatalogCreate: performanceCatalogCreateSchema,
  RoleProfileCreate: roleProfileCreateSchema,
  PerformanceCatalogPatch: performanceCatalogPatchSchema,
  SkillContent: skillContentSchema,
  EvaluationContent: evaluationContentSchema,
  RoleProfileContent: roleProfileContentSchema,
  SkillVersionCreate: skillVersionCreateSchema,
  EvaluationVersionCreate: evaluationVersionCreateSchema,
  RoleVersionCreate: roleVersionCreateSchema,
  SkillVersionPatch: skillVersionPatchSchema,
  EvaluationVersionPatch: evaluationVersionPatchSchema,
  RoleVersionPatch: roleVersionPatchSchema,
  AllowedSeniorities: allowedSenioritiesSchema,
  ProfileAssignment: profileAssignmentSchema,
  CycleCreate: cycleCreateSchema,
  CyclePatch: cyclePatchSchema,
  WorkPlanCreate: workPlanCreateSchema,
  WorkPlanPatch: workPlanPatchSchema,
  WorkTaskCreate: workTaskCreateSchema,
  WorkTaskPatch: workTaskPatchSchema,
  WorkProgress: workProgressSchema,
  ReviewCreate: reviewCreateSchema,
  ReviewReviewer: reviewReviewerSchema,
  ReviewSubmissionPatch: reviewSubmissionPatchSchema,
  ReviewFinalize: reviewFinalizeSchema,
  ReviewCorrection: reviewCorrectionSchema,
  ResolvedProfile: resolvedProfileSchema,
  WorkTaskDefinition: workTaskDefinitionSchema,
  ReviewAnswers: reviewAnswersSchema,
  ReviewSnapshot: reviewSnapshotSchema
};

// src/access.ts
var hrPermissionCodes = [
  "departments.read",
  "departments.create",
  "departments.edit",
  "departments.archive",
  "departments.delete",
  "projects.read",
  "projects.create",
  "projects.edit",
  "projects.archive",
  "projects.delete",
  "positions.read",
  "positions.create",
  "positions.edit",
  "positions.archive",
  "positions.delete",
  "locations.read",
  "locations.create",
  "locations.edit",
  "locations.archive",
  "locations.delete",
  "seniorities.read",
  "seniorities.create",
  "seniorities.edit",
  "seniorities.archive",
  "seniorities.delete",
  "employees.create",
  "employees.edit",
  "employees.archive",
  "employees.delete",
  "employees.import"
];
var permissionCodes = [
  ...performancePermissions,
  ...hrPermissionCodes,
  "accounts.read",
  "accounts.manage",
  "permissions.manage",
  "service_accounts.manage",
  "workspace.read",
  "workspace.manage",
  "organization.read",
  "organization.manage",
  "employees.read",
  "employees.manage",
  "employee_fields.personal.read",
  "employee_fields.personal.write",
  "employee_fields.documents.read",
  "employee_fields.documents.write",
  "employee_fields.screening.read",
  "employee_fields.screening.write",
  "employee_fields.payroll.read",
  "employee_fields.payroll.write",
  "employee_fields.compensation.read",
  "employee_fields.compensation.write",
  "requisitions.read",
  "requisitions.manage",
  "requisitions.approve",
  "job_postings.read",
  "job_postings.manage",
  "job_postings.approve",
  "job_postings.publish",
  "candidates.read",
  "candidates.manage",
  "candidates.merge",
  "candidates.import",
  "privacy_notices.manage",
  "processing_basis_policies.manage",
  "interviews.read",
  "interviews.schedule",
  "interviews.feedback.own",
  "interviews.feedback.all",
  "offers.read",
  "offers.manage",
  "offers.approve",
  "onboarding.read",
  "onboarding.manage",
  "offboarding.read",
  "offboarding.manage",
  "offboarding.access.manage",
  "files.read",
  "files.upload",
  "exports.run",
  "audit.read",
  "retention.manage",
  "legal_holds.manage"
];
function permissionAlternatives(permission) {
  if (permission.startsWith("employees.") && !["employees.read", "employees.manage"].includes(permission))
    return [permission, "employees.manage"];
  if (hrPermissionCodes.includes(permission))
    return [permission, permission.endsWith(".read") ? "organization.read" : "organization.manage"];
  return [permission];
}
var workAreas = ["HR", "ADMINISTRATIVE"];
var permissionScopes = [
  "WORKSPACE",
  "ORGANIZATION",
  "DEPARTMENT",
  "TEAM",
  "SELF",
  "DIRECT_REPORTS",
  "ASSIGNED_ENTITIES"
];
function scopeMatches(principal, grant, target) {
  switch (grant.scope) {
    case "WORKSPACE":
      return principal.workspaceId === target.workspaceId;
    case "ORGANIZATION":
      return Boolean(grant.scopeEntityId) && grant.scopeEntityId === target.organizationId;
    case "DEPARTMENT":
      return Boolean(grant.scopeEntityId) && grant.scopeEntityId === target.departmentId;
    case "TEAM":
      return Boolean(grant.scopeEntityId) && grant.scopeEntityId === target.teamId;
    case "SELF":
      return target.ownerAccountId === principal.accountId;
    case "DIRECT_REPORTS":
      return target.lineManagerAccountIds?.includes(principal.accountId) ?? false;
    case "ASSIGNED_ENTITIES":
      return target.assignedAccountIds?.includes(principal.accountId) ?? false;
  }
}
function isAccessDenied(principal, permission, target) {
  return principal.workspaceId !== target.workspaceId || principal.overrides.some(
    (item) => item.effect === "DENY" && permissionAlternatives(permission).includes(item.permission) && (!item.targetEntityType || item.targetEntityType === target.entityType) && (!item.targetEntityId || item.targetEntityId === target.entityId) && scopeMatches(principal, item, target)
  );
}
function isAccessAllowed(principal, permission, target) {
  if (isAccessDenied(principal, permission, target)) return false;
  const relevantOverrides = principal.overrides.filter(
    (item) => permissionAlternatives(permission).includes(item.permission) && (!item.targetEntityType || item.targetEntityType === target.entityType) && (!item.targetEntityId || item.targetEntityId === target.entityId) && scopeMatches(principal, item, target)
  );
  if (relevantOverrides.some((item) => item.effect === "ALLOW")) return true;
  return principal.grants.some(
    (grant) => permissionAlternatives(permission).includes(grant.permission) && scopeMatches(principal, grant, target)
  );
}
var systemGroupNames = [
  "System Admin",
  "HR Admin",
  "Recruitment Admin",
  "Recruiter",
  "Hiring Manager",
  "Interviewer",
  "Onboarding/Offboarding Manager",
  "Employee"
];

// src/errors.ts
var errorCodes = [
  "ROLE_PROFILE_NOT_CONFIGURED",
  "ROLE_PROFILE_MISMATCH",
  "SENIORITY_IN_USE",
  "ACTIVE_PLAN_EXISTS",
  "REQUIRED_REVIEW_ANSWERS_MISSING",
  "REVIEW_DEADLINE_PASSED",
  "VALIDATION_FAILED",
  "AUTH_INVALID_CREDENTIALS",
  "AUTH_TOKEN_INVALID",
  "AUTH_TOKEN_EXPIRED",
  "AUTH_REFRESH_REUSED",
  "AUTH_REFRESH_REPLAY_MISMATCH",
  "AUTH_MFA_REQUIRED",
  "AUTH_MFA_INVALID",
  "AUTH_MFA_NOT_ENROLLED",
  "ACCOUNT_PENDING_ACTIVATION",
  "ACCOUNT_BLOCKED",
  "ACCOUNT_DISABLED",
  "CSRF_INVALID",
  "PERMISSION_DENIED",
  "RESOURCE_NOT_FOUND",
  "VERSION_CONFLICT",
  "IDEMPOTENCY_KEY_REUSED",
  "INVALID_STATE_TRANSITION",
  "SELF_APPROVAL_FORBIDDEN",
  "EMAIL_ALREADY_EXISTS",
  "ORGANIZATION_ALREADY_EXISTS",
  "NOTIFICATION_PREFERENCE_LOCKED",
  "DUPLICATE_CANDIDATE",
  "ACTIVE_MEMBERS_BLOCK_ARCHIVE",
  "OBJECT_IN_USE",
  "IMMUTABLE_VERSION",
  "LEGAL_HOLD_ACTIVE",
  "PROCESSING_BASIS_REQUIRED",
  "PROCESSING_BASIS_UNRESOLVED",
  "PROCESSING_RESTRICTED",
  "PRIVACY_NOTICE_REQUIRED",
  "OFFER_LINK_EXPIRED",
  "OFFER_LINK_REVOKED",
  "FILE_TOO_LARGE",
  "FILE_TYPE_UNSUPPORTED",
  "FILE_NOT_CLEAN",
  "FILE_INFECTED",
  "FILE_REUPLOAD_REQUIRED",
  "UPLOAD_SESSION_INVALID",
  "UPLOAD_SESSION_EXPIRED",
  "RATE_LIMITED",
  "PROVIDER_NOT_CONFIGURED",
  "PROVIDER_FAILED",
  "SERVICE_UNAVAILABLE"
];
var DomainError = class extends Error {
  constructor(code, status, message, details) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
    this.name = "DomainError";
  }
};

// src/notifications.ts
var notificationCategories = [
  "ADMINISTRATIVE",
  "PERFORMANCE",
  "SECURITY_LOGIN",
  "SECURITY_ACCOUNT",
  "ACCESS",
  "HR_PROFILE",
  "RECRUITMENT",
  "APPROVALS",
  "INTERVIEWS",
  "OFFERS",
  "ONBOARDING",
  "OFFBOARDING",
  "FILES",
  "SYSTEM"
];
var notificationCategoryPolicies = [
  {
    category: "ADMINISTRATIVE",
    mandatory: false,
    defaultEmailEnabled: false,
    defaultInAppEnabled: true
  },
  {
    category: "PERFORMANCE",
    mandatory: false,
    defaultEmailEnabled: false,
    defaultInAppEnabled: true
  },
  {
    category: "SECURITY_LOGIN",
    mandatory: true,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "SECURITY_ACCOUNT",
    mandatory: true,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "ACCESS",
    mandatory: true,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "HR_PROFILE",
    mandatory: false,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "RECRUITMENT",
    mandatory: false,
    defaultEmailEnabled: false,
    defaultInAppEnabled: true
  },
  {
    category: "APPROVALS",
    mandatory: false,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "INTERVIEWS",
    mandatory: false,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "OFFERS",
    mandatory: false,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "ONBOARDING",
    mandatory: false,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "OFFBOARDING",
    mandatory: false,
    defaultEmailEnabled: true,
    defaultInAppEnabled: true
  },
  {
    category: "FILES",
    mandatory: false,
    defaultEmailEnabled: false,
    defaultInAppEnabled: true
  },
  {
    category: "SYSTEM",
    mandatory: false,
    defaultEmailEnabled: false,
    defaultInAppEnabled: true
  }
];
function notificationPolicy(category) {
  const policy = notificationCategoryPolicies.find((item) => item.category === category);
  if (!policy) throw new Error(`\u041D\u0435\u0438\u0437\u0432\u0435\u0441\u0442\u043D\u0430\u044F \u043A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044F \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0439: ${category}`);
  return policy;
}

// src/schemas.ts
import { z as z2 } from "zod";
var uuidSchema = z2.uuid();
var localDateSchema = z2.iso.date();
var timestampSchema = z2.iso.datetime({ offset: true });
var emailSchema = z2.email().max(320).transform((value) => value.trim().toLowerCase());
var passwordSchema = z2.string().min(12).max(128);
var idempotencyKeySchema = z2.string().min(8).max(128);
var cursorQuerySchema = z2.object({
  cursor: z2.string().min(1).max(2048).optional(),
  limit: z2.coerce.number().int().min(1).max(100).default(25),
  search: z2.string().trim().max(200).optional(),
  sort: z2.string().max(100).optional()
});
var loginRequestSchema = z2.object({
  workspace: z2.string().min(1).max(100),
  login: emailSchema,
  password: passwordSchema
});
var createAccountSchema = z2.object({
  login: emailSchema,
  employeeId: uuidSchema.nullish()
});
var versionedCommandSchema = z2.object({
  version: z2.number().int().positive(),
  reason: z2.string().trim().min(1).max(2e3).optional()
});
var createEmployeeSchema = z2.object({
  organizationId: uuidSchema,
  primaryDepartmentId: uuidSchema,
  primaryTeamId: uuidSchema,
  jobFunctionId: uuidSchema.nullish(),
  specialisationId: uuidSchema.nullish(),
  seniorityId: uuidSchema,
  locationId: uuidSchema,
  workName: z2.string().trim().min(1).max(200),
  workEmail: emailSchema,
  positionId: uuidSchema.nullish(),
  additionalTeamIds: z2.array(uuidSchema).default([]),
  jobTitle: z2.string().trim().max(200).optional(),
  employmentType: z2.enum(["FULL_TIME", "PART_TIME", "CONTRACTOR"]).default("FULL_TIME"),
  workFormat: z2.enum(["OFFICE", "REMOTE", "HYBRID"]).default("REMOTE"),
  workTelegram: z2.string().trim().max(200).nullish(),
  personalTelegram: z2.string().trim().max(200).nullish(),
  status: z2.enum(["JOINING", "PROBATION", "ACTIVE", "ON_LEAVE", "OFFBOARDING", "INACTIVE", "ARCHIVED"]).default("JOINING"),
  startDate: localDateSchema,
  lineManagerId: uuidSchema.nullish(),
  functionalManagerId: uuidSchema.nullish(),
  buddyId: uuidSchema.nullish(),
  isOrganizationTopLevel: z2.boolean().default(false),
  isExternal: z2.boolean().default(false)
}).refine((value) => value.isOrganizationTopLevel || Boolean(value.lineManagerId), {
  path: ["lineManagerId"],
  message: "lineManagerId \u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u0435\u043D \u0434\u043B\u044F \u0441\u043E\u0442\u0440\u0443\u0434\u043D\u0438\u043A\u0430 \u043D\u0435 \u0432\u0435\u0440\u0445\u043D\u0435\u0433\u043E \u0443\u0440\u043E\u0432\u043D\u044F"
});
var createCandidateSchema = z2.object({
  organizationId: uuidSchema,
  displayName: z2.string().trim().min(1).max(200),
  primaryEmail: emailSchema,
  countryCode: z2.string().regex(/^[A-Z]{2}$/),
  source: z2.string().trim().min(1).max(100),
  purpose: z2.string().trim().min(1).max(100),
  jurisdiction: z2.string().trim().min(1).max(100),
  phone: z2.string().trim().max(50).nullish()
});
function encodeCursor(value) {
  return Buffer.from(JSON.stringify(value), "utf8").toString("base64url");
}
function decodeCursor(cursor) {
  return z2.record(z2.string(), z2.string()).parse(JSON.parse(Buffer.from(cursor, "base64url").toString("utf8")));
}

// src/state-machines.ts
var accountTransitions = {
  PENDING_ACTIVATION: ["ACTIVE", "DISABLED"],
  ACTIVE: ["BLOCKED", "DISABLED"],
  BLOCKED: ["ACTIVE", "DISABLED"],
  DISABLED: ["PENDING_ACTIVATION", "ACTIVE"]
};
var approvalTransitions = {
  DRAFT: ["PENDING_APPROVAL"],
  PENDING_APPROVAL: ["APPROVED", "REJECTED"],
  APPROVED: ["DRAFT"],
  REJECTED: ["DRAFT"]
};
var lifecycleTransitions = {
  OPEN: ["ON_HOLD", "CLOSED"],
  ON_HOLD: ["OPEN", "CLOSED"],
  CLOSED: ["OPEN", "ARCHIVED"],
  ARCHIVED: []
};
var channelPublicationTransitions = {
  UNPUBLISHED: ["PUBLISHING"],
  PUBLISHING: ["PUBLISHED", "FAILED"],
  PUBLISHED: ["UNPUBLISHING"],
  UNPUBLISHING: ["UNPUBLISHED", "FAILED"],
  FAILED: ["PUBLISHING"]
};
var applicationTransitions = {
  PENDING_FILE_SCAN: ["ACTIVE", "REJECTED"],
  ACTIVE: ["REJECTED", "WITHDRAWN", "OFFER"],
  OFFER: ["ACTIVE", "WITHDRAWN", "HIRED"],
  REJECTED: ["ARCHIVED"],
  WITHDRAWN: ["ARCHIVED"],
  HIRED: ["ARCHIVED"],
  ARCHIVED: []
};
var interviewTransitions = {
  SCHEDULED: ["SCHEDULED", "COMPLETED", "CANCELLED", "NO_SHOW"],
  COMPLETED: [],
  CANCELLED: [],
  NO_SHOW: []
};
var offerLifecycleTransitions = {
  NOT_SENT: ["DELIVERY_QUEUED", "WITHDRAWN"],
  DELIVERY_QUEUED: ["SENDING", "WITHDRAWN", "EXPIRED"],
  SENDING: ["SENT", "DELIVERY_FAILED", "WITHDRAWN", "EXPIRED"],
  DELIVERY_FAILED: ["DELIVERY_QUEUED", "WITHDRAWN", "EXPIRED"],
  SENT: ["VIEWED", "ACCEPTED", "DECLINED", "WITHDRAWN", "EXPIRED"],
  VIEWED: ["ACCEPTED", "DECLINED", "WITHDRAWN", "EXPIRED"],
  ACCEPTED: [],
  DECLINED: [],
  WITHDRAWN: [],
  EXPIRED: []
};
var employeeTransitions = {
  JOINING: ["PROBATION", "ACTIVE", "OFFBOARDING", "INACTIVE", "ARCHIVED"],
  PROBATION: ["ACTIVE", "ON_LEAVE", "OFFBOARDING", "INACTIVE", "ARCHIVED"],
  ACTIVE: ["PROBATION", "ON_LEAVE", "OFFBOARDING", "INACTIVE", "ARCHIVED"],
  ON_LEAVE: ["PROBATION", "ACTIVE", "OFFBOARDING", "INACTIVE", "ARCHIVED"],
  OFFBOARDING: ["INACTIVE", "ACTIVE", "JOINING", "ARCHIVED"],
  INACTIVE: ["JOINING", "ARCHIVED"],
  ARCHIVED: ["JOINING", "INACTIVE"]
};
var onboardingTransitions = {
  PENDING: ["IN_PROGRESS", "CANCELLED"],
  IN_PROGRESS: ["COMPLETED", "CANCELLED"],
  COMPLETED: [],
  CANCELLED: []
};
var offboardingTransitions = {
  PENDING: ["IN_PROGRESS", "CANCELLED"],
  IN_PROGRESS: ["COMPLETED", "CANCELLED"],
  COMPLETED: ["CANCELLED"],
  CANCELLED: []
};
function assertTransition(table, from, to) {
  if (!table[from].includes(to)) {
    throw new DomainError("INVALID_STATE_TRANSITION", 409, `\u041F\u0435\u0440\u0435\u0445\u043E\u0434 ${from} -> ${to} \u0437\u0430\u043F\u0440\u0435\u0449\u0451\u043D`);
  }
}
function aggregatePublicationStatus(statuses) {
  if (statuses.length === 0 || statuses.every((item) => item === "UNPUBLISHED"))
    return "UNPUBLISHED";
  if (statuses.some((item) => item === "PUBLISHING" || item === "UNPUBLISHING"))
    return "PUBLISHING";
  if (statuses.every((item) => item === "PUBLISHED")) return "PUBLISHED";
  if (statuses.some((item) => item === "PUBLISHED")) return "PARTIALLY_PUBLISHED";
  return "FAILED";
}

// src/performance-responses.ts
import { z as z3 } from "zod";
var id = z3.uuid();
var date = z3.iso.datetime();
var version = z3.number().int().positive();
var base = { id, organizationId: id, version, createdAt: date, updatedAt: date };
var performanceCatalogResponseSchema = z3.strictObject({
  ...base,
  code: z3.string(),
  name: z3.string(),
  ownerEmployeeId: id.nullable(),
  specialisationId: id.nullable(),
  positionId: id.nullable().optional(),
  status: z3.enum(["ACTIVE", "ARCHIVED"]),
  currentPublishedVersionId: id.nullable()
});
var performanceVersionResponseSchema = z3.strictObject({
  ...base,
  catalogId: id,
  kind: z3.enum(["SKILL", "ROLE_PROFILE", "EVALUATION"]),
  versionNumber: version,
  status: z3.enum(["DRAFT", "PUBLISHED", "RETIRED"]),
  publishedAt: date.nullable(),
  allowedActions: z3.array(z3.string()),
  content: z3.union([skillContentSchema, roleProfileContentSchema, evaluationContentSchema])
});
var assignmentResponseSchema = z3.strictObject({
  id,
  employeeId: id,
  organizationId: id,
  roleProfileVersionId: id,
  seniorityId: id,
  validFrom: date,
  validUntil: date.nullable(),
  reason: z3.string(),
  profile: resolvedProfileSchema
});
var profileResolutionResponseSchema = z3.strictObject({
  status: z3.enum(["AVAILABLE", "NOT_CONFIGURED", "ASSIGNED"]),
  currentAssignment: assignmentResponseSchema.nullable(),
  suggestedVersion: performanceVersionResponseSchema.nullable(),
  updateAvailable: z3.boolean(),
  allowedActions: z3.array(z3.string())
});
var allowedSenioritiesResponseSchema = z3.strictObject({
  specialisationId: id,
  version,
  seniorityIds: z3.array(id)
});
var cycleResponseSchema = z3.strictObject({
  ...base,
  name: z3.string(),
  status: z3.enum(["DRAFT", "ACTIVE", "CLOSED", "CANCELLED"]),
  periodStart: z3.iso.date(),
  periodEnd: z3.iso.date(),
  reviewOpenAt: date,
  reviewDueAt: date
});
var workPlanResponseSchema = z3.strictObject({
  ...base,
  employeeId: id,
  cycleId: id,
  assignmentId: id,
  title: z3.string(),
  status: z3.enum(["DRAFT", "ACTIVE", "IN_REVIEW", "COMPLETED", "CANCELLED"]),
  profile: resolvedProfileSchema,
  roleChanged: z3.boolean(),
  allowedActions: z3.array(z3.string()),
  replacesPlanId: id.nullable()
});
var workTaskResponseSchema = z3.strictObject({
  ...base,
  planId: id,
  origin: z3.enum(["TEMPLATE", "CUSTOM"]),
  sourceTaskKey: z3.string().nullable(),
  originalDefinition: workTaskDefinitionSchema,
  definition: workTaskDefinitionSchema,
  dueDate: z3.iso.date(),
  status: z3.enum(["TODO", "IN_PROGRESS", "SUBMITTED", "ACCEPTED", "EXCLUDED"]),
  result: z3.string(),
  evidence: z3.array(z3.string()),
  reason: z3.string().nullable(),
  acceptedAt: date.nullable(),
  acceptedByAccountId: id.nullable(),
  allowedActions: z3.array(z3.string())
});
var submissionResponseSchema = z3.strictObject({
  id,
  reviewId: id,
  reviewerType: z3.enum(["SELF", "LINE_MANAGER"]),
  reviewerAccountId: id.nullable(),
  version,
  revision: version,
  status: z3.enum(["DRAFT", "SUBMITTED"]),
  submittedAt: date.nullable(),
  content: reviewAnswersSchema
});
var scoringResponseSchema = z3.strictObject({
  recommendedScore: z3.number().min(1).max(5).nullable(),
  recommendedGrade: z3.number().int().min(1).max(5).nullable(),
  coverage: z3.number().min(0).max(100)
});
var reviewResponseSchema = z3.strictObject({
  ...base,
  employeeId: id,
  planId: id,
  status: z3.enum(["DRAFT", "IN_PROGRESS", "READY", "PUBLISHED", "CANCELLED"]),
  isCurrent: z3.boolean(),
  correctsReviewId: id.nullable(),
  reviewDueAt: date,
  snapshot: reviewSnapshotSchema,
  ...scoringResponseSchema.shape,
  finalGrade: z3.number().int().min(1).max(5).nullable(),
  finalReason: z3.string().nullable(),
  publishedAt: date.nullable(),
  submissions: z3.array(submissionResponseSchema),
  reviewerOptions: z3.array(z3.strictObject({ id, name: z3.string() })).optional(),
  allowedActions: z3.array(z3.string())
});
var performancePageSchema = (item) => z3.strictObject({
  items: z3.array(item),
  page: z3.strictObject({ hasMore: z3.boolean(), nextCursor: z3.string().nullable() })
});
var performanceAllSchemas = {
  ...performanceSchemas,
  PerformanceCatalogResponse: performanceCatalogResponseSchema,
  PerformanceVersionResponse: performanceVersionResponseSchema,
  AssignmentResponse: assignmentResponseSchema,
  ProfileResolutionResponse: profileResolutionResponseSchema,
  AllowedSenioritiesResponse: allowedSenioritiesResponseSchema,
  CycleResponse: cycleResponseSchema,
  WorkPlanResponse: workPlanResponseSchema,
  WorkTaskResponse: workTaskResponseSchema,
  SubmissionResponse: submissionResponseSchema,
  ReviewResponse: reviewResponseSchema,
  PerformanceCatalogPage: performancePageSchema(performanceCatalogResponseSchema),
  PerformanceVersionPage: performancePageSchema(performanceVersionResponseSchema),
  AssignmentPage: performancePageSchema(assignmentResponseSchema),
  CyclePage: performancePageSchema(cycleResponseSchema),
  WorkPlanPage: performancePageSchema(workPlanResponseSchema),
  WorkTaskPage: performancePageSchema(workTaskResponseSchema),
  ReviewPage: performancePageSchema(reviewResponseSchema)
};

// src/performance-routes.ts
var routes = [];
var add = (method, path, handler, response, permissions, request, kind) => {
  routes.push({
    method,
    path: `/api/v1${path}`,
    handler,
    response,
    permissions,
    operationId: `performance${method}${path.replaceAll(/[^a-zA-Z0-9]+/g, "_")}`,
    ...request ? { request } : {},
    ...kind ? { kind } : {}
  });
};
for (const [kind, plural, versionPlural, prefix] of [
  ["SKILL", "skills", "skill-versions", "Skill"],
  ["ROLE_PROFILE", "role-profiles", "role-profile-versions", "Role"],
  [
    "EVALUATION",
    "employee-evaluation-templates",
    "employee-evaluation-template-versions",
    "Evaluation"
  ]
]) {
  const read = ["performance_templates.read"];
  const manage = ["performance_templates.manage"];
  add("GET", `/${plural}`, "catalogList", "PerformanceCatalogPage", read, void 0, kind);
  add(
    "POST",
    `/${plural}`,
    "catalogCreate",
    "PerformanceCatalogResponse",
    manage,
    kind === "ROLE_PROFILE" ? "RoleProfileCreate" : "PerformanceCatalogCreate",
    kind
  );
  add("GET", `/${plural}/{id}`, "catalogGet", "PerformanceCatalogResponse", read, void 0, kind);
  add(
    "PATCH",
    `/${plural}/{id}`,
    "catalogPatch",
    "PerformanceCatalogResponse",
    manage,
    "PerformanceCatalogPatch",
    kind
  );
  add(
    "POST",
    `/${plural}/{id}/archive`,
    "catalogArchive",
    "PerformanceCatalogResponse",
    manage,
    "PerformanceReason",
    kind
  );
  add(
    "GET",
    `/${plural}/{id}/versions`,
    "versionList",
    "PerformanceVersionPage",
    read,
    void 0,
    kind
  );
  add(
    "POST",
    `/${plural}/{id}/versions`,
    "versionCreate",
    "PerformanceVersionResponse",
    manage,
    `${prefix}VersionCreate`,
    kind
  );
  add(
    "GET",
    `/${versionPlural}/{id}`,
    "versionGet",
    "PerformanceVersionResponse",
    read,
    void 0,
    kind
  );
  add(
    "PATCH",
    `/${versionPlural}/{id}`,
    "versionPatch",
    "PerformanceVersionResponse",
    manage,
    `${prefix}VersionPatch`,
    kind
  );
  add(
    "POST",
    `/${versionPlural}/{id}/publish`,
    "versionPublish",
    "PerformanceVersionResponse",
    manage,
    "PerformanceAction",
    kind
  );
  add(
    "POST",
    `/${versionPlural}/{id}/retire`,
    "versionRetire",
    "PerformanceVersionResponse",
    manage,
    "PerformanceReason",
    kind
  );
}
add("GET", "/role-profile-versions/{id}/preview", "profilePreview", "ResolvedProfile", [
  "performance_templates.read"
]);
add(
  "GET",
  "/specialisations/{id}/allowed-seniorities",
  "senioritiesGet",
  "AllowedSenioritiesResponse",
  ["organization.read"]
);
add(
  "PUT",
  "/specialisations/{id}/allowed-seniorities",
  "senioritiesPut",
  "AllowedSenioritiesResponse",
  ["organization.manage"],
  "AllowedSeniorities"
);
add(
  "GET",
  "/employees/{id}/role-profile-resolution",
  "profileResolution",
  "ProfileResolutionResponse",
  ["work_plans.read", "work_plans.manage"]
);
add("GET", "/employees/{id}/role-profile-assignments", "assignmentList", "AssignmentPage", [
  "work_plans.read",
  "work_plans.manage"
]);
add(
  "POST",
  "/employees/{id}/role-profile-assignments",
  "assignmentCreate",
  "AssignmentResponse",
  ["work_plans.manage"],
  "ProfileAssignment"
);
add("GET", "/performance-cycles", "cycleList", "CyclePage", [
  "performance_reviews.manage",
  "work_plans.manage"
]);
add(
  "POST",
  "/performance-cycles",
  "cycleCreate",
  "CycleResponse",
  ["performance_reviews.manage"],
  "CycleCreate"
);
add("GET", "/performance-cycles/{id}", "cycleGet", "CycleResponse", [
  "performance_reviews.manage",
  "work_plans.manage"
]);
add(
  "PATCH",
  "/performance-cycles/{id}",
  "cyclePatch",
  "CycleResponse",
  ["performance_reviews.manage"],
  "CyclePatch"
);
for (const action of ["activate", "close", "cancel"])
  add(
    "POST",
    `/performance-cycles/{id}/${action}`,
    `cycle_${action}`,
    "CycleResponse",
    ["performance_reviews.manage"],
    action === "cancel" ? "PerformanceReason" : "PerformanceAction"
  );
add("GET", "/employees/{id}/work-plans", "planList", "WorkPlanPage", [
  "work_plans.read",
  "work_plans.manage"
]);
add(
  "POST",
  "/employees/{id}/work-plans",
  "planCreate",
  "WorkPlanResponse",
  ["work_plans.manage"],
  "WorkPlanCreate"
);
add("GET", "/work-plans/{id}", "planGet", "WorkPlanResponse", [
  "work_plans.read",
  "work_plans.manage"
]);
add(
  "PATCH",
  "/work-plans/{id}",
  "planPatch",
  "WorkPlanResponse",
  ["work_plans.manage"],
  "WorkPlanPatch"
);
for (const action of ["activate", "cancel"])
  add(
    "POST",
    `/work-plans/{id}/${action}`,
    `plan_${action}`,
    "WorkPlanResponse",
    ["work_plans.manage"],
    action === "cancel" ? "PerformanceReason" : "PerformanceAction"
  );
add("GET", "/work-plans/{id}/tasks", "taskList", "WorkTaskPage", [
  "work_plans.read",
  "work_plans.manage"
]);
add(
  "POST",
  "/work-plans/{id}/tasks",
  "taskCreate",
  "WorkTaskResponse",
  ["work_plans.manage"],
  "WorkTaskCreate"
);
add(
  "PATCH",
  "/work-tasks/{id}",
  "taskPatch",
  "WorkTaskResponse",
  ["work_plans.manage"],
  "WorkTaskPatch"
);
add(
  "PATCH",
  "/work-tasks/{id}/progress",
  "taskProgress",
  "WorkTaskResponse",
  ["work_plans.update_own"],
  "WorkProgress"
);
for (const action of ["start", "submit", "accept", "return", "exclude"])
  add(
    "POST",
    `/work-tasks/{id}/${action}`,
    `task_${action}`,
    "WorkTaskResponse",
    [action === "start" || action === "submit" ? "work_plans.update_own" : "work_plans.manage"],
    action === "submit" ? "WorkProgress" : action === "exclude" || action === "return" ? "PerformanceReason" : "PerformanceAction"
  );
add(
  "POST",
  "/work-plans/{id}/reviews",
  "reviewCreate",
  "ReviewResponse",
  ["performance_reviews.manage"],
  "ReviewCreate"
);
add("GET", "/employees/{id}/reviews", "reviewList", "ReviewPage", ["performance_reviews.read"]);
add("GET", "/reviews/{id}", "reviewGet", "ReviewResponse", ["performance_reviews.read"]);
add(
  "POST",
  "/reviews/{id}/open",
  "reviewOpen",
  "ReviewResponse",
  ["performance_reviews.manage"],
  "PerformanceAction"
);
add(
  "POST",
  "/reviews/{id}/reviewer",
  "reviewReviewer",
  "ReviewResponse",
  ["performance_reviews.manage"],
  "ReviewReviewer"
);
add("GET", "/reviews/{id}/submissions/{type}", "submissionGet", "SubmissionResponse", [
  "performance_reviews.read"
]);
add(
  "PATCH",
  "/reviews/{id}/submissions/{type}",
  "submissionPatch",
  "SubmissionResponse",
  ["performance_reviews.write_own"],
  "ReviewSubmissionPatch"
);
add(
  "POST",
  "/reviews/{id}/submissions/{type}/submit",
  "submissionSubmit",
  "SubmissionResponse",
  ["performance_reviews.write_own"],
  "PerformanceAction"
);
add(
  "POST",
  "/reviews/{id}/submissions/{type}/return",
  "submissionReturn",
  "SubmissionResponse",
  ["performance_reviews.manage"],
  "PerformanceReason"
);
add(
  "POST",
  "/reviews/{id}/finalize",
  "reviewFinalize",
  "ReviewResponse",
  ["performance_reviews.publish"],
  "ReviewFinalize"
);
add(
  "POST",
  "/reviews/{id}/publish",
  "reviewPublish",
  "ReviewResponse",
  ["performance_reviews.publish"],
  "PerformanceAction"
);
add(
  "POST",
  "/reviews/{id}/cancel",
  "reviewCancel",
  "ReviewResponse",
  ["performance_reviews.manage"],
  "PerformanceReason"
);
add(
  "POST",
  "/reviews/{id}/corrections",
  "reviewCorrection",
  "ReviewResponse",
  ["performance_reviews.manage"],
  "ReviewCorrection"
);
var performanceRoutes = routes;

// src/performance-rules.ts
function invalidPerformance(field, message) {
  throw new DomainError("VALIDATION_FAILED", 400, message, { fieldErrors: [{ field, message }] });
}
function uniquePerformance(values, field) {
  if (new Set(values).size !== values.length) invalidPerformance(field, "\u041F\u043E\u0432\u0442\u043E\u0440\u044F\u044E\u0449\u0438\u0435\u0441\u044F \u044D\u043B\u0435\u043C\u0435\u043D\u0442\u044B");
}
function completeWeights(values, field) {
  if (!values.length || values.reduce((sum, item) => sum + item.weight, 0) !== 1e4 || values.some((item) => item.weight <= 0)) {
    invalidPerformance(field, "\u041F\u043E\u043B\u043E\u0436\u0438\u0442\u0435\u043B\u044C\u043D\u044B\u0435 \u0432\u0435\u0441\u0430 \u0434\u043E\u043B\u0436\u043D\u044B \u0441\u043E\u0441\u0442\u0430\u0432\u043B\u044F\u0442\u044C 10000");
  }
}
function validateSkillPublication(content) {
  if (!content.description.trim()) invalidPerformance("description", "\u0414\u043E\u0431\u0430\u0432\u044C\u0442\u0435 \u043E\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u043D\u0430\u0432\u044B\u043A\u0430");
  uniquePerformance(
    content.levels.map((item) => item.level),
    "levels"
  );
  if (content.levels.length !== 5 || content.levels.some((level) => !level.indicators.length))
    invalidPerformance("levels", "\u041E\u043F\u0438\u0448\u0438\u0442\u0435 \u0432\u0441\u0435 \u043F\u044F\u0442\u044C \u0443\u0440\u043E\u0432\u043D\u0435\u0439 \u043D\u0430\u0432\u044B\u043A\u0430");
}
function validateEvaluationPublication(content) {
  if (Object.values(content.sectionWeights).reduce((sum, value) => sum + value, 0) !== 1e4)
    invalidPerformance("sectionWeights", "\u0421\u0443\u043C\u043C\u0430 \u0432\u0435\u0441\u043E\u0432 \u0440\u0430\u0437\u0434\u0435\u043B\u043E\u0432 \u0434\u043E\u043B\u0436\u043D\u0430 \u0431\u044B\u0442\u044C 10000");
  uniquePerformance(
    content.grades.map((grade) => String(grade.grade)),
    "grades"
  );
  if (content.grades.length !== 5 || content.grades.some((grade) => !grade.description.trim()))
    invalidPerformance("grades", "\u041E\u043F\u0438\u0448\u0438\u0442\u0435 \u043F\u044F\u0442\u044C \u043E\u0446\u0435\u043D\u043E\u043A");
  uniquePerformance(
    content.masteryMapping.map((item) => `${item.expected}:${item.actual}`),
    "masteryMapping"
  );
  if (content.masteryMapping.length !== 25)
    invalidPerformance("masteryMapping", "\u041D\u0443\u0436\u043D\u0430 \u043F\u043E\u043B\u043D\u0430\u044F \u0442\u0430\u0431\u043B\u0438\u0446\u0430 \u0441\u043E\u043E\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0438\u044F \u0443\u0440\u043E\u0432\u043D\u0435\u0439");
  for (const expected of masteryLevels) {
    let previous = 0;
    for (const actual of masteryLevels) {
      const entry = content.masteryMapping.find(
        (item) => item.expected === expected && item.actual === actual
      );
      if (!entry || entry.grade < previous || expected === actual && entry.grade !== 3)
        invalidPerformance("masteryMapping", "\u041D\u0435\u043A\u043E\u0440\u0440\u0435\u043A\u0442\u043D\u0430\u044F \u0448\u043A\u0430\u043B\u0430 \u0432\u043B\u0430\u0434\u0435\u043D\u0438\u044F \u043D\u0430\u0432\u044B\u043A\u043E\u043C");
      previous = entry.grade;
    }
  }
  uniquePerformance(
    content.values.map((item) => item.key),
    "values"
  );
  if (content.sectionWeights.VALUES > 0) completeWeights(content.values, "values");
  uniquePerformance(
    content.questionGroups.map((item) => item.key),
    "questionGroups"
  );
  const questions = content.questionGroups.flatMap((group) => group.questions);
  if (questions.length > 100) invalidPerformance("questionGroups", "\u041D\u0435 \u0431\u043E\u043B\u0435\u0435 100 \u0432\u043E\u043F\u0440\u043E\u0441\u043E\u0432");
  uniquePerformance(
    questions.map((item) => item.key),
    "questions"
  );
  for (const question of questions) {
    uniquePerformance(question.options, `questions.${question.key}.options`);
    if (question.type === "CHOICE" && question.options.length < 2)
      invalidPerformance(`questions.${question.key}`, "\u041D\u0443\u0436\u043D\u044B \u0432\u0430\u0440\u0438\u0430\u043D\u0442\u044B \u043E\u0442\u0432\u0435\u0442\u0430");
  }
}
function validateTaskDefinition(task) {
  if (!task.expectedResult.trim() || !task.acceptanceCriteria.trim())
    invalidPerformance(`tasks.${task.taskKey}`, "\u041E\u043F\u0438\u0448\u0438\u0442\u0435 \u0440\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442 \u0438 \u043F\u0440\u0438\u0451\u043C\u043A\u0443 \u0437\u0430\u0434\u0430\u0447\u0438");
  uniquePerformance(
    task.criteria.map((item) => item.key),
    `tasks.${task.taskKey}.criteria`
  );
  if (task.criteria.length !== 3 || !["QUALITY", "COMPLEXITY", "TIMELINESS"].every(
    (key2) => task.criteria.some((criterion) => criterion.key === key2 && criterion.description.trim())
  ))
    invalidPerformance(
      `tasks.${task.taskKey}.criteria`,
      "\u041D\u0443\u0436\u043D\u044B \u043A\u0440\u0438\u0442\u0435\u0440\u0438\u0438 \u043A\u0430\u0447\u0435\u0441\u0442\u0432\u0430, \u0441\u043B\u043E\u0436\u043D\u043E\u0441\u0442\u0438 \u0438 \u0441\u0440\u043E\u043A\u043E\u0432"
    );
  completeWeights(task.criteria, `tasks.${task.taskKey}.criteria`);
}
function validateRolePublication(content) {
  if (!content.mission.trim() || !content.evaluationTemplateVersionId || !content.seniorityIds.length || !content.tasks.length)
    invalidPerformance("content", "\u0417\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u043D\u0430\u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435, \u0443\u0440\u043E\u0432\u043D\u0438, \u0437\u0430\u0434\u0430\u0447\u0438 \u0438 \u0444\u043E\u0440\u043C\u0443 \u043E\u0446\u0435\u043D\u043A\u0438");
  uniquePerformance(content.seniorityIds, "seniorityIds");
  uniquePerformance(
    content.tasks.map((item) => item.taskKey),
    "tasks"
  );
  for (const task of content.tasks) {
    uniquePerformance(
      task.levels.map((item) => item.seniorityId),
      `tasks.${task.taskKey}.levels`
    );
    if (!task.levels.length)
      invalidPerformance(`tasks.${task.taskKey}.levels`, "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0443\u0440\u043E\u0432\u043D\u0438 \u0437\u0430\u0434\u0430\u0447\u0438");
    for (const level of task.levels) {
      if (!content.seniorityIds.includes(level.seniorityId))
        invalidPerformance("tasks.levels", "\u0423\u0440\u043E\u0432\u0435\u043D\u044C \u043D\u0435 \u0432\u0445\u043E\u0434\u0438\u0442 \u0432 \u043F\u0440\u043E\u0444\u0438\u043B\u044C");
      validateTaskDefinition({
        ...task,
        expectedResult: level.expectedResult,
        weight: level.weight,
        criteria: level.criteria
      });
    }
  }
  uniquePerformance(
    content.skillExpectations.map((item) => `${item.skillVersionId}:${item.seniorityId}`),
    "skillExpectations"
  );
  if (content.skillExpectations.some((item) => !content.seniorityIds.includes(item.seniorityId)))
    invalidPerformance("skillExpectations", "\u0423\u0440\u043E\u0432\u0435\u043D\u044C \u043D\u0435 \u0432\u0445\u043E\u0434\u0438\u0442 \u0432 \u043F\u0440\u043E\u0444\u0438\u043B\u044C");
  for (const seniority of content.seniorityIds) {
    completeWeights(
      content.tasks.flatMap(
        (task) => task.levels.filter((level) => level.seniorityId === seniority)
      ),
      `tasks.${seniority}`
    );
    const skills = content.skillExpectations.filter((item) => item.seniorityId === seniority);
    if (skills.length > 100)
      invalidPerformance("skillExpectations", "\u041D\u0435 \u0431\u043E\u043B\u0435\u0435 100 \u043D\u0430\u0432\u044B\u043A\u043E\u0432 \u043D\u0430 \u0443\u0440\u043E\u0432\u0435\u043D\u044C");
    completeWeights(skills, `skillExpectations.${seniority}`);
  }
}
function reviewQuestionRules(snapshot, reviewer) {
  const rules = /* @__PURE__ */ new Map();
  for (const task of snapshot.tasks.filter((task2) => task2.status !== "EXCLUDED")) {
    for (const criterion of task.definition.criteria)
      rules.set(`task:${task.id}:${criterion.key}`, {
        type: "NUMBER",
        required: criterion.required,
        employeeVisible: criterion.employeeVisible
      });
  }
  for (const skill of snapshot.profile.skills)
    rules.set(`skill:${skill.skillId}`, {
      type: "MASTERY",
      required: skill.required,
      employeeVisible: true
    });
  for (const value of snapshot.profile.evaluationTemplate.values)
    rules.set(`value:${value.key}`, {
      type: "NUMBER",
      required: value.required,
      employeeVisible: value.employeeVisible
    });
  for (const group of snapshot.profile.evaluationTemplate.questionGroups.filter(
    (group2) => group2.reviewerTypes.includes(reviewer)
  )) {
    for (const question of group.questions)
      rules.set(`question:${question.key}`, {
        type: question.type === "SCALE" ? "NUMBER" : question.type,
        required: question.required,
        employeeVisible: question.employeeVisible,
        options: question.options
      });
  }
  return rules;
}
function validateReviewAnswers(snapshot, content, reviewer, submitting = false) {
  const rules = reviewQuestionRules(snapshot, reviewer);
  uniquePerformance(
    content.answers.map((answer) => answer.key),
    "answers"
  );
  for (const answer of content.answers) {
    const rule = rules.get(answer.key);
    if (!rule) invalidPerformance(`answers.${answer.key}`, "\u041D\u0435\u0438\u0437\u0432\u0435\u0441\u0442\u043D\u044B\u0439 \u043A\u0440\u0438\u0442\u0435\u0440\u0438\u0439");
    if (answer.value === null || answer.value === "DONT_KNOW") continue;
    const valid = rule.type === "NUMBER" ? typeof answer.value === "number" : rule.type === "MASTERY" ? masteryLevels.includes(answer.value) : rule.type === "CHOICE" ? rule.options?.includes(String(answer.value)) : typeof answer.value === "string";
    if (!valid) invalidPerformance(`answers.${answer.key}`, "\u041E\u0442\u0432\u0435\u0442 \u043D\u0435 \u0441\u043E\u043E\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0443\u0435\u0442 \u0448\u043A\u0430\u043B\u0435");
  }
  if (submitting) {
    const missing = [...rules].filter(
      ([key2, rule]) => rule.required && !content.answers.some(
        (answer) => answer.key === key2 && answer.value !== null && answer.value !== "DONT_KNOW" && (typeof answer.value !== "string" || answer.value.trim())
      )
    );
    if (missing.length)
      throw new DomainError(
        "REQUIRED_REVIEW_ANSWERS_MISSING",
        409,
        "\u0417\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u044B\u0435 \u043A\u0440\u0438\u0442\u0435\u0440\u0438\u0438",
        { keys: missing.map(([key2]) => key2) }
      );
  }
}
function fraction(n, d = 1n) {
  if (d === 0n) throw new Error("Zero denominator");
  let a = n < 0n ? -n : n;
  let b = d;
  while (b) {
    const r = a % b;
    a = b;
    b = r;
  }
  return { n: n / (a || 1n), d: d / (a || 1n) };
}
function add2(a, b) {
  return fraction(a.n * b.d + b.n * a.d, a.d * b.d);
}
function scale(a, n, d = 1) {
  return fraction(a.n * BigInt(n), a.d * BigInt(d));
}
function mean(values) {
  const all = values.reduce((sum, item) => sum + item.weight, 0);
  const known = values.filter(
    (item) => item.value !== null
  );
  const count = known.reduce((sum, item) => sum + item.weight, 0);
  return {
    value: count ? scale(
      known.reduce((sum, item) => add2(sum, scale(item.value, item.weight)), fraction(0n)),
      1,
      count
    ) : null,
    coverage: all ? fraction(BigInt(count), BigInt(all)) : fraction(0n)
  };
}
function rounded(value) {
  return Number((value.n * 200n + value.d) / (2n * value.d)) / 100;
}
function calculateReview(snapshot, answers) {
  validateReviewAnswers(snapshot, answers, "LINE_MANAGER");
  const byKey = new Map(answers.answers.map((answer) => [answer.key, answer.value]));
  const number = (key2) => {
    const value = byKey.get(key2);
    return typeof value === "number" ? fraction(BigInt(value)) : null;
  };
  const tasks = snapshot.tasks.filter((task) => task.status !== "EXCLUDED").map((task) => ({
    weight: task.definition.weight,
    ...mean(
      task.definition.criteria.map((criterion) => ({
        weight: criterion.weight,
        value: number(`task:${task.id}:${criterion.key}`)
      }))
    )
  }));
  const deliverables = mean(tasks);
  const deliverablesCoverage = tasks.reduce(
    (sum, task) => add2(sum, scale(task.coverage, task.weight, 1e4)),
    fraction(0n)
  );
  const skills = mean(
    snapshot.profile.skills.map((skill) => {
      const actual = byKey.get(`skill:${skill.skillId}`);
      const entry = snapshot.profile.evaluationTemplate.masteryMapping.find(
        (entry2) => entry2.expected === skill.expectedLevel && entry2.actual === actual
      );
      return { weight: skill.weight, value: entry ? fraction(BigInt(entry.grade)) : null };
    })
  );
  const values = mean(
    snapshot.profile.evaluationTemplate.values.map((value) => ({
      weight: value.weight,
      value: number(`value:${value.key}`)
    }))
  );
  const weights = snapshot.profile.evaluationTemplate.sectionWeights;
  const sections = [
    { ...deliverables, coverage: deliverablesCoverage, weight: weights.DELIVERABLES },
    { ...skills, weight: weights.SKILLS },
    { ...values, weight: weights.VALUES }
  ].filter((section) => section.weight > 0);
  const coverage = rounded(
    scale(
      sections.reduce(
        (sum, section) => add2(sum, scale(section.coverage, section.weight, 1e4)),
        fraction(0n)
      ),
      100
    )
  );
  if (sections.some((section) => !section.value))
    return { recommendedScore: null, recommendedGrade: null, coverage };
  const score = mean(sections).value;
  if (!score) return { recommendedScore: null, recommendedGrade: null, coverage };
  const grade = [3n, 5n, 7n, 9n].findIndex((bound) => score.n * 2n < bound * score.d);
  return {
    recommendedScore: rounded(score),
    recommendedGrade: grade < 0 ? 5 : grade + 1,
    coverage
  };
}

// src/administrative-domain.ts
var administrativeCurrencies = ["USD", "EUR", "RUB"];
var administrativeResourceKinds = ["SERVER", "DOMAIN", "SERVICE", "PROXY"];
var administrativePeriodUnits = ["DAY", "MONTH", "YEAR"];
var invalid = (message) => {
  throw new DomainError("VALIDATION_FAILED", 400, message);
};
var dayMilliseconds = 864e5;
var maximumMinor = 999999999999999999n;
function administrativeMoneyToMinor(value, allowNegative = false) {
  if (typeof value !== "string" || value.length > 64)
    return invalid("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0434\u0435\u043D\u0435\u0436\u043D\u0443\u044E \u0441\u0443\u043C\u043C\u0443 \u0441 \u0442\u043E\u0447\u043D\u043E\u0441\u0442\u044C\u044E \u0434\u043E \u0434\u0432\u0443\u0445 \u0437\u043D\u0430\u043A\u043E\u0432.");
  const cleaned = value.trim().replace(/[ \u00a0\u202f]/g, "").replace(",", ".");
  if (!/^-?\d+(?:\.\d{1,2})?$/.test(cleaned) || !allowNegative && cleaned.startsWith("-"))
    return invalid("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0434\u043E\u043F\u0443\u0441\u0442\u0438\u043C\u0443\u044E \u0441\u0443\u043C\u043C\u0443 \u0441 \u0442\u043E\u0447\u043D\u043E\u0441\u0442\u044C\u044E \u0434\u043E \u0434\u0432\u0443\u0445 \u0437\u043D\u0430\u043A\u043E\u0432.");
  const negative = cleaned.startsWith("-");
  const [whole, fraction2 = ""] = cleaned.replace(/^-/, "").split(".");
  const minor = BigInt(whole) * 100n + BigInt(fraction2.padEnd(2, "0"));
  if (minor > maximumMinor) return invalid("\u0414\u0435\u043D\u0435\u0436\u043D\u0430\u044F \u0441\u0443\u043C\u043C\u0430 \u0441\u043B\u0438\u0448\u043A\u043E\u043C \u0432\u0435\u043B\u0438\u043A\u0430.");
  return negative ? -minor : minor;
}
function administrativeMinorToMoney(value) {
  const absolute = value < 0n ? -value : value;
  if (absolute > maximumMinor) return invalid("\u0414\u0435\u043D\u0435\u0436\u043D\u0430\u044F \u0441\u0443\u043C\u043C\u0430 \u0441\u043B\u0438\u0448\u043A\u043E\u043C \u0432\u0435\u043B\u0438\u043A\u0430.");
  return `${value < 0n ? "-" : ""}${absolute / 100n}.${(absolute % 100n).toString().padStart(2, "0")}`;
}
function normalizeAdministrativeMoney(value, allowNegative = false) {
  return administrativeMinorToMoney(administrativeMoneyToMinor(value, allowNegative));
}
function administrativeMoneyDisplay(value, allowNegative = false) {
  const normalized = normalizeAdministrativeMoney(value, allowNegative);
  const [whole, fraction2] = normalized.split(".");
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "\xA0")},${fraction2}`;
}
function dateParts(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return invalid("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0434\u0430\u0442\u0443 \u0432 \u0444\u043E\u0440\u043C\u0430\u0442\u0435 \u0413\u0413\u0413\u0413-\u041C\u041C-\u0414\u0414.");
  const [year, month, day] = value.split("-").map(Number);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > monthDays(year, month))
    return invalid("\u0423\u043A\u0430\u0437\u0430\u043D\u0430 \u043D\u0435\u0441\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u044E\u0449\u0430\u044F \u043A\u0430\u043B\u0435\u043D\u0434\u0430\u0440\u043D\u0430\u044F \u0434\u0430\u0442\u0430.");
  const date2 = /* @__PURE__ */ new Date(0);
  date2.setUTCFullYear(year, month - 1, day);
  date2.setUTCHours(0, 0, 0, 0);
  return { year, month, day, epoch: date2.getTime() / dayMilliseconds };
}
function monthDays(year, month) {
  return [
    31,
    year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31
  ][month - 1];
}
function dateString(year, month, day) {
  if (year < 1 || year > 9999)
    return invalid("\u0414\u0430\u0442\u0430 \u0432\u044B\u0445\u043E\u0434\u0438\u0442 \u0437\u0430 \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u043C\u044B\u0439 \u043A\u0430\u043B\u0435\u043D\u0434\u0430\u0440\u043D\u044B\u0439 \u0434\u0438\u0430\u043F\u0430\u0437\u043E\u043D.");
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
function administrativeToday(timeZone, now = /* @__PURE__ */ new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(now);
    const part = (type) => parts.find((item) => item.type === type)?.value;
    const result = `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
    dateParts(result);
    return result;
  } catch {
    return invalid("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0447\u0430\u0441\u043E\u0432\u043E\u0439 \u043F\u043E\u044F\u0441 \u0438 \u043A\u0430\u043B\u0435\u043D\u0434\u0430\u0440\u043D\u0443\u044E \u0434\u0430\u0442\u0443.");
  }
}
function administrativeDateDifference(targetDate, today) {
  return dateParts(targetDate).epoch - dateParts(today).epoch;
}
function administrativeExpiry(date2, today) {
  dateParts(today);
  if (date2 === null) return { state: "UNKNOWN", days: null };
  const days = administrativeDateDifference(date2, today);
  return { state: days > 0 ? "ACTIVE" : days === 0 ? "TODAY" : "EXPIRED", days };
}
function administrativeAddPeriod(anchor, period, multiplier = 1) {
  const parts = dateParts(anchor);
  if (!Number.isSafeInteger(period.count) || period.count < 1 || !administrativePeriodUnits.includes(period.unit) || !Number.isSafeInteger(multiplier) || multiplier < 0)
    return invalid("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043F\u043E\u043B\u043E\u0436\u0438\u0442\u0435\u043B\u044C\u043D\u0443\u044E \u0434\u043B\u0438\u0442\u0435\u043B\u044C\u043D\u043E\u0441\u0442\u044C \u043F\u0435\u0440\u0438\u043E\u0434\u0430 \u043E\u043F\u043B\u0430\u0442\u044B.");
  const delta = period.count * multiplier;
  if (!Number.isSafeInteger(delta)) return invalid("\u041F\u0435\u0440\u0438\u043E\u0434 \u043E\u043F\u043B\u0430\u0442\u044B \u0441\u043B\u0438\u0448\u043A\u043E\u043C \u0432\u0435\u043B\u0438\u043A.");
  if (period.unit === "DAY") {
    const date2 = new Date((parts.epoch + delta) * dayMilliseconds);
    if (!Number.isFinite(date2.getTime())) return invalid("\u041F\u0435\u0440\u0438\u043E\u0434 \u043E\u043F\u043B\u0430\u0442\u044B \u0441\u043B\u0438\u0448\u043A\u043E\u043C \u0432\u0435\u043B\u0438\u043A.");
    return dateString(date2.getUTCFullYear(), date2.getUTCMonth() + 1, date2.getUTCDate());
  }
  const totalMonths = parts.year * 12 + parts.month - 1 + delta * (period.unit === "YEAR" ? 12 : 1);
  const year = Math.floor(totalMonths / 12), month = totalMonths % 12 + 1;
  return dateString(year, month, Math.min(parts.day, monthDays(year, month)));
}
function administrativePeriodDates(anchor, period, from, to) {
  const initial = dateParts(anchor), start = dateParts(from), end = dateParts(to);
  if (start.epoch > end.epoch) return invalid("\u041D\u0430\u0447\u0430\u043B\u043E \u043F\u0435\u0440\u0438\u043E\u0434\u0430 \u0434\u043E\u043B\u0436\u043D\u043E \u0431\u044B\u0442\u044C \u043D\u0435 \u043F\u043E\u0437\u0436\u0435 \u043E\u043A\u043E\u043D\u0447\u0430\u043D\u0438\u044F.");
  administrativeAddPeriod(anchor, period, 0);
  const monthStep = period.count * (period.unit === "YEAR" ? 12 : 1);
  const distance = period.unit === "DAY" ? start.epoch - initial.epoch : (start.year - initial.year) * 12 + start.month - initial.month;
  let index = Math.max(
    0,
    Math.floor(distance / (period.unit === "DAY" ? period.count : monthStep)) - 1
  );
  const results = [];
  const maximumDay = dateParts("9999-12-31").epoch;
  for (let visited = 0; visited < 50002; visited++, index++) {
    if (period.unit === "DAY" ? initial.epoch + index * period.count > maximumDay : initial.year * 12 + initial.month - 1 + index * monthStep > 9999 * 12 + 11)
      return results;
    const occurrence = administrativeAddPeriod(anchor, period, index);
    if (occurrence > to) return results;
    if (occurrence >= from) results.push(occurrence);
    if (results.length > 5e4) return invalid("\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043C\u0435\u043D\u044C\u0448\u0438\u0439 \u043F\u0435\u0440\u0438\u043E\u0434 \u0434\u043B\u044F \u0440\u0430\u0441\u0447\u0451\u0442\u0430 \u043E\u043F\u043B\u0430\u0442.");
  }
  return invalid("\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043C\u0435\u043D\u044C\u0448\u0438\u0439 \u043F\u0435\u0440\u0438\u043E\u0434 \u0434\u043B\u044F \u0440\u0430\u0441\u0447\u0451\u0442\u0430 \u043E\u043F\u043B\u0430\u0442.");
}
function normalizedHost(value) {
  if (!value || /[\s/?#@%]/.test(value)) return invalid("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0430\u0434\u0440\u0435\u0441 \u043F\u0440\u043E\u043A\u0441\u0438.");
  try {
    const url = new URL(`http://${value}/`);
    if (!url.hostname || url.port || url.username || url.password || url.pathname !== "/")
      return invalid("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0430\u0434\u0440\u0435\u0441 \u043F\u0440\u043E\u043A\u0441\u0438.");
    const host = url.hostname.replace(/^\[|\]$/g, "").toLowerCase();
    if (host.length > 253) return invalid("\u0410\u0434\u0440\u0435\u0441 \u043F\u0440\u043E\u043A\u0441\u0438 \u0441\u043B\u0438\u0448\u043A\u043E\u043C \u0434\u043B\u0438\u043D\u043D\u044B\u0439.");
    return host;
  } catch {
    return invalid("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0430\u0434\u0440\u0435\u0441 \u043F\u0440\u043E\u043A\u0441\u0438.");
  }
}
function authority(value) {
  const match = value.match(/^(\[[^\]]+\]|[^:\[\]]+):(\d{1,5})$/);
  if (!match)
    return invalid("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0430\u0434\u0440\u0435\u0441 \u0438 \u043F\u043E\u0440\u0442 \u043F\u0440\u043E\u043A\u0441\u0438. \u0414\u043B\u044F IPv6 \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0439\u0442\u0435 \u0430\u0434\u0440\u0435\u0441 \u0432 \u043A\u0432\u0430\u0434\u0440\u0430\u0442\u043D\u044B\u0445 \u0441\u043A\u043E\u0431\u043A\u0430\u0445.");
  const port = Number(match[2]);
  if (port < 1 || port > 65535) return invalid("\u041F\u043E\u0440\u0442 \u043F\u0440\u043E\u043A\u0441\u0438 \u0434\u043E\u043B\u0436\u0435\u043D \u0431\u044B\u0442\u044C \u043E\u0442 1 \u0434\u043E 65535.");
  return { host: normalizedHost(match[1]), port };
}
function userInfo(value) {
  const divider = value.indexOf(":");
  if (divider < 0) return invalid("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043B\u043E\u0433\u0438\u043D \u0438 \u043F\u0430\u0440\u043E\u043B\u044C \u043F\u0440\u043E\u043A\u0441\u0438 \u0447\u0435\u0440\u0435\u0437 \u0434\u0432\u043E\u0435\u0442\u043E\u0447\u0438\u0435.");
  try {
    const login = decodeURIComponent(value.slice(0, divider)), password = decodeURIComponent(value.slice(divider + 1));
    if (login.length > 512 || password.length > 2048 || /[\u0000-\u001f\u007f]/.test(login + password))
      return invalid("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0434\u0430\u043D\u043D\u044B\u0435 \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043F\u0440\u043E\u043A\u0441\u0438.");
    return { login, password };
  } catch {
    return invalid("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u043A\u043E\u0434\u0438\u0440\u043E\u0432\u043A\u0443 \u0434\u0430\u043D\u043D\u044B\u0445 \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043F\u0440\u043E\u043A\u0441\u0438.");
  }
}
function parseAdministrativeProxy(input) {
  if (typeof input !== "string" || input.length > 4096 || /[\u0000-\u001f\u007f]/.test(input))
    return invalid("\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043E\u0434\u043D\u0443 \u0441\u0442\u0440\u043E\u043A\u0443 \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043F\u0440\u043E\u043A\u0441\u0438.");
  let value = input.trim(), protocol = "HTTP";
  const scheme = value.match(/^([a-z][a-z\d+.-]*):\/\//i);
  if (scheme) {
    const selected = scheme[1].toUpperCase();
    if (selected !== "HTTP" && selected !== "HTTPS" && selected !== "SOCKS5")
      return invalid("\u041F\u043E\u0434\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u044E\u0442\u0441\u044F \u043F\u0440\u043E\u0442\u043E\u043A\u043E\u043B\u044B HTTP, HTTPS \u0438 SOCKS5.");
    protocol = selected;
    value = value.slice(scheme[0].length);
    if (/[/?#]/.test(value))
      return invalid("\u0421\u0442\u0440\u043E\u043A\u0430 \u043F\u0440\u043E\u043A\u0441\u0438 \u043D\u0435 \u0434\u043E\u043B\u0436\u043D\u0430 \u0441\u043E\u0434\u0435\u0440\u0436\u0430\u0442\u044C \u043F\u0443\u0442\u044C \u0438\u043B\u0438 \u043F\u0430\u0440\u0430\u043C\u0435\u0442\u0440\u044B URL.");
  }
  const at = value.lastIndexOf("@");
  if (at >= 0)
    return { protocol, ...authority(value.slice(at + 1)), ...userInfo(value.slice(0, at)) };
  if (scheme) return { protocol, ...authority(value), login: null, password: null };
  if (value.startsWith("[")) return { protocol, ...authority(value), login: null, password: null };
  const fields = value.split(":");
  if (fields.length === 2) return { protocol, ...authority(value), login: null, password: null };
  if (fields.length >= 4)
    return {
      protocol,
      ...authority(`${fields[0]}:${fields[1]}`),
      ...userInfo(fields.slice(2).join(":"))
    };
  return invalid("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0444\u043E\u0440\u043C\u0430\u0442 \u0441\u0442\u0440\u043E\u043A\u0438 \u043F\u0440\u043E\u043A\u0441\u0438: \u0430\u0434\u0440\u0435\u0441:\u043F\u043E\u0440\u0442 \u0438\u043B\u0438 \u0430\u0434\u0440\u0435\u0441:\u043F\u043E\u0440\u0442:\u043B\u043E\u0433\u0438\u043D:\u043F\u0430\u0440\u043E\u043B\u044C.");
}
var parseProxyConnection = parseAdministrativeProxy;
function formatAdministrativeProxy(proxy, revealCredentials = false) {
  const host = proxy.host.includes(":") ? `[${proxy.host}]` : proxy.host;
  const credentials = proxy.login !== null || proxy.password !== null ? `${revealCredentials ? encodeURIComponent(proxy.login ?? "") : "\u2022\u2022\u2022\u2022"}:${revealCredentials ? encodeURIComponent(proxy.password ?? "") : "\u2022\u2022\u2022\u2022"}@` : "";
  return `${proxy.protocol.toLowerCase()}://${credentials}${host}:${proxy.port}`;
}

// src/hr-operations.ts
import { z as z4 } from "zod";
var hrEmployeeStatuses = [
  "JOINING",
  "PROBATION",
  "ACTIVE",
  "ON_LEAVE",
  "INACTIVE",
  "OFFBOARDING",
  "ARCHIVED"
];
var hrEmploymentTypes = ["FULL_TIME", "PART_TIME", "CONTRACTOR"];
var hrWorkFormats = ["OFFICE", "HYBRID", "REMOTE"];
var hrEmployeeBulkPatchSchema = z4.object({
  departmentId: z4.uuid().optional(),
  primaryTeamId: z4.uuid().nullable().optional(),
  additionalTeamIds: z4.array(z4.uuid()).max(50).optional(),
  lineManagerId: z4.uuid().nullable().optional(),
  status: z4.enum(hrEmployeeStatuses).optional(),
  positionId: z4.uuid().nullable().optional(),
  locationId: z4.uuid().optional(),
  employmentType: z4.enum(hrEmploymentTypes).optional(),
  workFormat: z4.enum(hrWorkFormats).optional()
}).strict();
var hrEmployeeBulkSchema = z4.object({
  employees: z4.array(z4.object({ id: z4.uuid(), version: z4.number().int().positive() }).strict()).min(1).max(250),
  patch: hrEmployeeBulkPatchSchema,
  reason: z4.string().trim().min(1).max(2e3)
}).strict();
var hrImportPreviewSchema = z4.object({
  organizationId: z4.uuid(),
  csv: z4.string().min(1).max(1e6)
}).strict();
var hrImportSchema = hrImportPreviewSchema.extend({
  dryRun: z4.boolean().optional().default(false)
});
var hrSearchSchema = z4.object({
  q: z4.string().trim().min(2).max(100),
  cursor: z4.string().min(1).optional(),
  limit: z4.coerce.number().int().min(1).max(50).default(20)
});
var hrImportTemplateColumns = [
  "\u041F\u043E\u0440\u044F\u0434\u043A\u043E\u0432\u044B\u0439 \u043D\u043E\u043C\u0435\u0440 \u0441\u043E\u0442\u0440\u0443\u0434\u043D\u0438\u043A\u0430",
  "\u0418\u043C\u044F",
  "\u0420\u0430\u0431\u043E\u0447\u0430\u044F \u043F\u043E\u0447\u0442\u0430",
  "\u0420\u0430\u0431\u043E\u0447\u0438\u0439 Telegram",
  "\u041B\u0438\u0447\u043D\u044B\u0439 Telegram",
  "\u041E\u0442\u0434\u0435\u043B",
  "\u041F\u0440\u043E\u0435\u043A\u0442",
  "\u0414\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u044C",
  "\u0423\u0440\u043E\u0432\u0435\u043D\u044C",
  "\u0420\u0443\u043A\u043E\u0432\u043E\u0434\u0438\u0442\u0435\u043B\u044C",
  "\u041B\u043E\u043A\u0430\u0446\u0438\u044F",
  "\u0414\u0430\u0442\u0430 \u0432\u044B\u0445\u043E\u0434\u0430",
  "\u0422\u0438\u043F \u0437\u0430\u043D\u044F\u0442\u043E\u0441\u0442\u0438",
  "\u0424\u043E\u0440\u043C\u0430\u0442 \u0440\u0430\u0431\u043E\u0442\u044B"
];
export {
  DomainError,
  accountTransitions,
  administrativeAddPeriod,
  administrativeCurrencies,
  administrativeDateDifference,
  administrativeExpiry,
  administrativeMinorToMoney,
  administrativeMoneyDisplay,
  administrativeMoneyToMinor,
  administrativePeriodDates,
  administrativePeriodUnits,
  administrativeResourceKinds,
  administrativeToday,
  aggregatePublicationStatus,
  allowedSenioritiesResponseSchema,
  allowedSenioritiesSchema,
  applicationTransitions,
  approvalTransitions,
  assertTransition,
  assignmentResponseSchema,
  calculateReview,
  channelPublicationTransitions,
  completeWeights,
  createAccountSchema,
  createCandidateSchema,
  createEmployeeSchema,
  cursorQuerySchema,
  cycleCreateSchema,
  cyclePatchSchema,
  cycleResponseSchema,
  decodeCursor,
  emailSchema,
  employeeTransitions,
  encodeCursor,
  errorCodes,
  evaluationContentSchema,
  evaluationVersionCreateSchema,
  evaluationVersionPatchSchema,
  formatAdministrativeProxy,
  hrEmployeeBulkPatchSchema,
  hrEmployeeBulkSchema,
  hrEmployeeStatuses,
  hrEmploymentTypes,
  hrImportPreviewSchema,
  hrImportSchema,
  hrImportTemplateColumns,
  hrPermissionCodes,
  hrSearchSchema,
  hrWorkFormats,
  idempotencyKeySchema,
  interviewTransitions,
  invalidPerformance,
  isAccessAllowed,
  isAccessDenied,
  lifecycleTransitions,
  localDateSchema,
  loginRequestSchema,
  masteryLevels,
  masterySchema,
  normalizeAdministrativeMoney,
  notificationCategories,
  notificationCategoryPolicies,
  notificationPolicy,
  offboardingTransitions,
  offerLifecycleTransitions,
  onboardingTransitions,
  parseAdministrativeProxy,
  parseProxyConnection,
  passwordSchema,
  performanceActionSchema,
  performanceAllSchemas,
  performanceCatalogCreateSchema,
  performanceCatalogPatchSchema,
  performanceCatalogResponseSchema,
  performanceCriterionSchema,
  performanceEvidenceSchema,
  performanceListSchema,
  performancePageSchema,
  performancePermissions,
  performanceReasonSchema,
  performanceRoutes,
  performanceSchemas,
  performanceTextSchema,
  performanceUrlSchema,
  performanceVersionResponseSchema,
  performanceVersionSchema,
  permissionAlternatives,
  permissionCodes,
  permissionScopes,
  profileAssignmentSchema,
  profileResolutionResponseSchema,
  resolvedProfileSchema,
  reviewAnswerSchema,
  reviewAnswersSchema,
  reviewCorrectionSchema,
  reviewCreateSchema,
  reviewFinalizeSchema,
  reviewQuestionRules,
  reviewResponseSchema,
  reviewReviewerSchema,
  reviewSnapshotSchema,
  reviewSubmissionPatchSchema,
  roleProfileContentSchema,
  roleProfileCreateSchema,
  roleSkillExpectationSchema,
  roleTaskTemplateSchema,
  roleVersionCreateSchema,
  roleVersionPatchSchema,
  scoringResponseSchema,
  skillContentSchema,
  skillVersionCreateSchema,
  skillVersionPatchSchema,
  submissionResponseSchema,
  systemGroupNames,
  timestampSchema,
  uniquePerformance,
  uuidSchema,
  validateEvaluationPublication,
  validateReviewAnswers,
  validateRolePublication,
  validateSkillPublication,
  validateTaskDefinition,
  versionedCommandSchema,
  workAreas,
  workPlanCreateSchema,
  workPlanPatchSchema,
  workPlanResponseSchema,
  workProgressSchema,
  workTaskCreateSchema,
  workTaskDefinitionSchema,
  workTaskPatchSchema,
  workTaskResponseSchema,
  workTaskSnapshotSchema
};
